import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import AttachmentsModal from '../components/AttachmentsModal';
import NotesModal from '../components/NotesModal';
import UploadModal from '../components/UploadModal';
import InstructionsModal from '../components/InstructionsModal';
import { fmtDate } from '../utils/date';

export default function GISDashboard() {
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [attachJob, setAttachJob] = useState(null);
  const [noteJob, setNoteJob] = useState(null);
  const [uploadJob, setUploadJob] = useState(null);
  const [instrJob, setInstrJob] = useState(null);
  // Health of the BLVD Field instructions feed — only surfaced when something is wrong
  const [instrStatus, setInstrStatus] = useState(null);

  useEffect(() => { fetchJobs(); fetchInstrStatus(); }, []);

  async function fetchJobs() {
    try {
      const { data } = await api.get('/gis/jobs');
      setJobs(data);
    } finally {
      setLoading(false);
    }
  }

  async function fetchInstrStatus() {
    try {
      const { data } = await api.get('/gis/instructions/status');
      setInstrStatus(data);
    } catch {
      /* non-essential */
    }
  }

  async function syncNow() {
    setSyncing(true);
    try {
      await api.post('/gis/sync');
      await Promise.all([fetchJobs(), fetchInstrStatus()]);
    } catch (err) {
      console.error('Sync failed:', err);
    } finally {
      setSyncing(false);
    }
  }

  async function updateStatus(id, status) {
    setUpdating(id);
    try {
      const { data } = await api.patch(`/gis/jobs/${id}/status`, { status });
      setJobs(prev => prev.map(j => j.id === id ? { ...j, ...data } : j));
    } catch (err) {
      console.error(err);
    } finally {
      setUpdating(null);
    }
  }

  async function retryUpload(id) {
    setUpdating(id);
    try {
      const { data } = await api.post(`/gis/jobs/${id}/resync-upload`);
      setJobs(prev => prev.map(j => j.id === id ? { ...j, ...data } : j));
    } catch (err) {
      console.error('Retry failed:', err);
    } finally {
      setUpdating(null);
    }
  }

  async function toggleRush(id, val) {
    try {
      const { data } = await api.patch(`/gis/jobs/${id}/rush`, { is_rush: val });
      setJobs(prev => prev.map(j => j.id === id ? { ...j, ...data } : j));
    } catch (err) {
      console.error('Toggle rush failed:', err);
    }
  }

  async function toggleFiberCheck(id, val) {
    try {
      const { data } = await api.patch(`/gis/jobs/${id}/fiber-check`, { checked: val });
      setJobs(prev => prev.map(j => j.id === id ? { ...j, ...data } : j));
    } catch (err) {
      console.error('Toggle fiber check failed:', err);
    }
  }

  async function saveNote(id, text) {
    try {
      const { data } = await api.patch(`/gis/jobs/${id}/notes`, { notes: text });
      setJobs(prev => prev.map(j => j.id === id ? { ...j, ...data } : j));
    } catch (err) {
      console.error('Save note failed:', err);
    }
  }

  // A search looks across every status, so nobody has to re-search on each tab
  const searching = search.trim() !== '';
  const pickFilter = (key) => { setFilter(key); setSearch(''); };

  const filtered = jobs
    .filter(j => searching || filter === 'all' ? true : filter === 'rush' ? (j.is_rush && j.status !== 'complete') : j.status === filter)
    .filter(j =>
      [j.project_number, j.customer, j.program, j.site_name, j.project_manager, j.upload_instructions]
        .join(' ').toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => (b.is_rush ? 1 : 0) - (a.is_rush ? 1 : 0)); // rush to the top

  const counts = {
    pending: jobs.filter(j => j.status === 'pending').length,
    in_progress: jobs.filter(j => j.status === 'in_progress').length,
    complete: jobs.filter(j => j.status === 'complete').length,
    rush: jobs.filter(j => j.is_rush && j.status !== 'complete').length,
  };

  const instrProblem = instrStatus && (
    !instrStatus.configured
      ? 'GIS Upload Instructions from BLVD Field are not being pulled — GIS_PORTAL_TOKEN is not set on the server.'
      : instrStatus.last_error
        ? `GIS Upload Instructions feed: ${instrStatus.last_error}`
        : null
  );

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-black text-white tracking-widest uppercase">Geospatial</h1>
            <p className="text-gray-400 text-sm mt-1">Projects scheduled for geospatial processing · auto-synced from Site Tracker</p>
          </div>
          <button
            onClick={syncNow}
            disabled={syncing}
            className="px-4 py-2 text-xs font-bold uppercase tracking-widest rounded border transition disabled:opacity-50"
            style={{ borderColor: '#c9a84c', color: '#c9a84c' }}
          >
            {syncing ? 'Syncing…' : '↻ Sync now'}
          </button>
        </div>

        {instrProblem && (
          <p className="text-xs text-amber-400 border border-amber-500/40 rounded px-3 py-2" style={{ backgroundColor: '#1a1a0d' }}>
            ⚠ {instrProblem}
          </p>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { key: 'pending', label: 'Pending', color: 'text-yellow-400' },
            { key: 'in_progress', label: 'In Progress', color: 'text-blue-400' },
            { key: 'rush', label: 'Rush', color: 'text-red-400' },
            { key: 'complete', label: 'Complete', color: 'text-green-400' },
          ].map(({ key, label, color }) => (
            <button
              key={key}
              onClick={() => pickFilter(key)}
              className={`border rounded-lg p-4 text-left transition ${
                filter === key && !searching ? 'border-gold-500' : 'border-navy-600 hover:border-navy-400'
              }`}
              style={{ backgroundColor: '#0d1b2e', borderColor: filter === key && !searching ? '#c9a84c' : '#1e3a5f' }}
            >
              <p className={`text-3xl font-black ${color}`}>{counts[key]}</p>
              <p className="text-xs text-gray-400 uppercase tracking-widest mt-1">{label}</p>
            </button>
          ))}
        </div>

        {/* Filter tabs + search */}
        <div className="flex items-center gap-4">
          <div className="flex gap-2">
            {['pending', 'in_progress', 'rush', 'complete', 'all'].map(f => (
              <button
                key={f}
                onClick={() => pickFilter(f)}
                className={`px-3 py-1 rounded text-xs font-bold uppercase tracking-widest transition ${
                  filter === f && !searching ? 'bg-gold-500 text-navy-900' : 'text-gray-400 hover:text-white'
                }`}
                style={filter === f && !searching ? { backgroundColor: '#c9a84c', color: '#0d1b2e' } : {}}
              >
                {f === 'in_progress' ? 'In Progress' : f}
              </button>
            ))}
          </div>

          <input
            type="text"
            placeholder="Search all statuses…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 border rounded px-4 py-1.5 text-sm text-white placeholder-gray-500 focus:outline-none transition"
            style={{ backgroundColor: '#142236', borderColor: '#1e3a5f' }}
          />
        </div>

        {searching && !loading && (
          <p className="text-xs text-gray-400">
            Searching Pending, In Progress and Complete · {filtered.length} match{filtered.length === 1 ? '' : 'es'}
          </p>
        )}

        {/* Jobs table */}
        {loading ? (
          <p className="text-gray-500 text-sm">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-gray-500 text-sm">
            {search ? `No jobs matching “${search}”.` : `No ${filter === 'all' ? '' : filter} jobs.`}
          </p>
        ) : (
          <div className="rounded-xl border overflow-x-auto" style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}>
            <table className="w-full text-sm">
              <thead className="border-b" style={{ backgroundColor: '#080f1a', borderColor: '#1e3a5f' }}>
                <tr>
                  {['', 'Project ID', 'Client', 'Program', 'Job Name', 'PM', 'Scheduled', 'Created', 'Status', 'Files', 'Instructions', 'Notes', ''].map((h, i) => (
                    <th key={i} className="text-left px-3 py-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: '#1e3a5f' }}>
                {filtered.map(job => {
                  const statusLabel = job.status === 'complete' ? 'COMPLETE' : job.status === 'in_progress' ? 'IN PROGRESS' : 'PENDING';
                  const statusColor = job.status === 'complete' ? 'text-green-400' : job.status === 'in_progress' ? 'text-blue-400' : 'text-yellow-400';
                  const isFiber = /fiber/i.test(job.program || '');
                  return (
                    <tr key={job.id} className={`transition hover:bg-white/5 ${job.is_rush ? 'border-l-2 border-red-500' : ''}`}>
                      {/* Rush toggle */}
                      <td className="px-2 py-3 w-8">
                        <button
                          title={job.is_rush ? 'Remove rush' : 'Mark rush'}
                          onClick={() => toggleRush(job.id, !job.is_rush)}
                          className={`text-sm transition ${job.is_rush ? 'text-red-400' : 'text-gray-600 hover:text-red-400'}`}
                        >
                          ⚡
                        </button>
                      </td>

                      {/* Project ID (+ fiber sync-check reminder) */}
                      <td className="px-3 py-3 font-mono text-xs text-gray-400 whitespace-nowrap">
                        {job.project_number}
                        {isFiber && (
                          <button
                            onClick={() => toggleFiberCheck(job.id, !job.fiber_sync_checked)}
                            title={job.fiber_sync_checked
                              ? 'Site Tracker map sync verified — click to undo'
                              : 'Fiber project — verify it is set up to sync in Site Tracker (Sitetracker Maps → Map Layers), then click to dismiss'}
                            className={`block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded border transition ${
                              job.fiber_sync_checked
                                ? 'border-green-500/40 text-green-500/70 hover:bg-green-500/10'
                                : 'border-amber-500/60 text-amber-400 hover:bg-amber-500/10'
                            }`}
                          >
                            {job.fiber_sync_checked ? '✓ Fiber · sync verified' : '⚠ Fiber · check ST sync'}
                          </button>
                        )}
                      </td>

                      {/* Client */}
                      <td className="px-3 py-3 text-gray-300 text-xs">{job.customer || '—'}</td>

                      {/* Program (from Site Tracker) */}
                      <td className="px-3 py-3 text-gray-400 text-xs whitespace-nowrap">{job.program || '—'}</td>

                      {/* Job Name */}
                      <td className="px-3 py-3 text-white font-medium text-xs">{job.site_name || '—'}</td>

                      {/* PM */}
                      <td className="px-3 py-3 text-gray-400 text-xs whitespace-nowrap">{job.project_manager || '—'}</td>

                      {/* Scheduled — the day the field work happens (Job Scheduled Start) */}
                      <td className="px-3 py-3 text-gray-300 text-xs whitespace-nowrap">
                        {fmtDate(job.scheduled_date)}
                      </td>

                      {/* Created — when the project was scheduled into the queue (Project Scheduled A) */}
                      <td className="px-3 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {fmtDate(job.project_scheduled_date)}
                      </td>

                      {/* Status */}
                      <td className="px-3 py-3">
                        <span className={`text-xs font-bold uppercase tracking-wide ${statusColor}`}>{statusLabel}</span>
                        {job.status === 'complete' && job.gis_upload_synced === false && (
                          <button
                            onClick={() => retryUpload(job.id)}
                            disabled={updating === job.id}
                            title="GIS Data Upload date failed to save to Site Tracker — click to retry"
                            className="block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded border border-red-500/50 text-red-400 hover:bg-red-500/10 transition"
                          >
                            {updating === job.id ? 'Retrying…' : '⚠ Not synced · Retry'}
                          </button>
                        )}
                      </td>

                      {/* Files */}
                      <td className="px-3 py-3">
                        {job.sf_id && (
                          <div className="flex items-center gap-1.5 whitespace-nowrap">
                            <button
                              onClick={() => setAttachJob(job)}
                              title="View Site Tracker attachments"
                              className="text-xs px-2 py-1 rounded border transition text-gray-400 hover:text-gold-400"
                              style={{ borderColor: '#1e3a5f' }}
                            >
                              📎 Files
                            </button>
                            <button
                              onClick={() => setUploadJob(job)}
                              title="Upload files to Site Tracker"
                              className="text-xs px-2 py-1 rounded border transition text-gray-400 hover:text-gold-400"
                              style={{ borderColor: '#1e3a5f' }}
                            >
                              ⤒ Upload
                            </button>
                          </div>
                        )}
                      </td>

                      {/* GIS Upload Instructions — written in BLVD Field; shown, never interpreted */}
                      <td className="px-3 py-3 max-w-[200px]">
                        {job.upload_instructions ? (
                          <button
                            onClick={() => setInstrJob(job)}
                            title={job.upload_instructions}
                            className="text-xs text-left truncate max-w-full font-semibold text-amber-300 hover:text-amber-200 transition"
                          >
                            📋 {job.upload_instructions.length > 28 ? `${job.upload_instructions.slice(0, 28)}…` : job.upload_instructions}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-600">—</span>
                        )}
                      </td>

                      {/* Notes */}
                      <td className="px-3 py-3 max-w-[200px]">
                        <button
                          onClick={() => setNoteJob(job)}
                          title={job.note || 'Add a note'}
                          className={`text-xs text-left truncate max-w-full transition ${job.note ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-400'}`}
                        >
                          {job.note ? (job.note.length > 28 ? `${job.note.slice(0, 28)}…` : job.note) : '+ note'}
                        </button>
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5 whitespace-nowrap">
                          {job.status === 'pending' && (
                            <button
                              onClick={() => updateStatus(job.id, 'in_progress')}
                              disabled={updating === job.id}
                              className="text-sm px-5 py-2 rounded font-bold uppercase tracking-wide transition disabled:opacity-50"
                              style={{ backgroundColor: '#1e3a5f', color: '#c9a84c', border: '1px solid #c9a84c' }}
                            >
                              {updating === job.id ? '…' : 'Start'}
                            </button>
                          )}
                          {job.status === 'in_progress' && (
                            <>
                              <button
                                onClick={() => updateStatus(job.id, 'complete')}
                                disabled={updating === job.id}
                                className="text-xs px-2 py-1 rounded border transition text-green-300 border-green-500/40 hover:bg-green-500/10 disabled:opacity-50"
                              >
                                {updating === job.id ? '…' : 'Complete'}
                              </button>
                              <button
                                onClick={() => updateStatus(job.id, 'pending')}
                                disabled={updating === job.id}
                                className="text-xs px-2 py-1 rounded border transition text-gray-500 hover:text-gray-300 disabled:opacity-50"
                                style={{ borderColor: '#1e3a5f' }}
                              >
                                Unstart
                              </button>
                            </>
                          )}
                          {job.status === 'complete' && (
                            <>
                              <span className="text-xs text-green-400 font-bold">✓ Complete</span>
                              <button
                                onClick={() => updateStatus(job.id, 'pending')}
                                disabled={updating === job.id}
                                className="text-xs px-2 py-1 rounded border transition text-gray-500 hover:text-gray-300 disabled:opacity-50"
                                style={{ borderColor: '#1e3a5f' }}
                              >
                                Reopen
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {attachJob && (
        <AttachmentsModal
          title={attachJob.project_number}
          subtitle={attachJob.customer}
          listUrl={`/gis/jobs/${attachJob.id}/st-attachments`}
          downloadUrl={(cvId) => `/gis/jobs/${attachJob.id}/st-attachments/${cvId}/download`}
          onClose={() => setAttachJob(null)}
        />
      )}

      {noteJob && (
        <NotesModal
          title={noteJob.project_number}
          subtitle={noteJob.customer}
          note={noteJob.note}
          onClose={() => setNoteJob(null)}
          onSave={(text) => saveNote(noteJob.id, text)}
        />
      )}

      {instrJob && (
        <InstructionsModal
          title={instrJob.project_number}
          subtitle={instrJob.customer}
          text={instrJob.upload_instructions}
          updatedAt={instrJob.upload_instructions_updated_at}
          onClose={() => setInstrJob(null)}
        />
      )}

      {uploadJob && (
        <UploadModal
          uploadUrl={`/gis/jobs/${uploadJob.id}/upload`}
          title={uploadJob.project_number}
          subtitle={uploadJob.customer}
          heading="Upload to Site Tracker"
          onClose={() => setUploadJob(null)}
          onUploaded={() => {}}
        />
      )}
    </Layout>
  );
}
